import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import { useExportOptions } from '@/hooks/useExportOptions';

import type { useGetCollectionTableProps } from './config';
import { buildCustomDatasourceRegisterToolbarConfig } from './registerToolbarConfig';
import type { CustomDatasourceTableFields } from './types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `getCounter(...)` → `(12)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  /** Navigates to the add-datasource page (owned by the page, shared with flag-off). */
  onCreate: () => void;
}

/**
 * Object toolbar for the Custom Datasources register, mounted only when the
 * `object_toolbar` flag is on. Owning `useExportOptions` here (rather than on the
 * page) keeps it off the legacy `ExportButton` path — with the flag off this
 * component never renders, so the export options are computed once, by whichever
 * path is actually shown (CUS-172). The register has no `CustomisableRibbon`, so
 * no ribbon filters are threaded into the PDF export.
 */
export const CustomDatasourceRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  onCreate,
}) => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'customDatasources',
  });
  const [showPdfModal, setShowPdfModal] = useState(false);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<CustomDatasourceTableFields>({
      tableProps,
      entityLabel: title,
      onShowCustomPdfModal,
    });

  const toolbarConfig = useMemo(
    () =>
      buildCustomDatasourceRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        create: { label: st('create_new_button'), onClick: onCreate },
      }),
    [title, counter, exportOptions, onCreate, st]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={false}
      />
    </>
  );
};
